import React from "react";
import {
  CompareToDateRangeStateType,
  DateRangeStateType,
  defaultCompareToDateRangeState,
  defaultDateRangeState,
} from "./CampaignState";

interface CampaignDateRangeContextValue {
  dateRanges: Record<string, DateRangeStateType>;
  compareToDateRanges: Record<string, CompareToDateRangeStateType>;
  setDateRange: (id: string, range: DateRangeStateType) => void;
  setCompareToDateRange: (
    id: string,
    range: CompareToDateRangeStateType
  ) => void;
}

const CampaignDateRangeContext =
  React.createContext<CampaignDateRangeContextValue>({
    dateRanges: {},
    compareToDateRanges: {},
    setDateRange: () => undefined,
    setCompareToDateRange: () => undefined,
  });

export const CampaignDateRangeProvider = ({ children }) => {
  const [dateRanges, setDateRanges] = React.useState<
    Record<string, DateRangeStateType>
  >({});
  const [compareToDateRanges, setCompareToDateRanges] = React.useState<
    Record<string, CompareToDateRangeStateType>
  >({});

  const setDateRange = React.useCallback(
    (id: string, range: DateRangeStateType) => {
      setDateRanges((prev) => ({ ...prev, [id]: range }));
    },
    []
  );

  const setCompareToDateRange = React.useCallback(
    (id: string, range: CompareToDateRangeStateType) => {
      setCompareToDateRanges((prev) => ({ ...prev, [id]: range }));
    },
    []
  );

  const value = React.useMemo(
    () => ({
      dateRanges,
      compareToDateRanges,
      setDateRange,
      setCompareToDateRange,
    }),
    [dateRanges, compareToDateRanges, setDateRange, setCompareToDateRange]
  );

  return (
    <CampaignDateRangeContext.Provider value={value}>
      {children}
    </CampaignDateRangeContext.Provider>
  );
};

export const useCampaignDateRange = (
  id: string
): [DateRangeStateType, (range: DateRangeStateType) => void] => {
  const { dateRanges, setDateRange } = React.useContext(
    CampaignDateRangeContext
  );
  const set = React.useCallback(
    (range: DateRangeStateType) => setDateRange(id, range),
    [id, setDateRange]
  );
  return [dateRanges[id] ?? defaultDateRangeState, set];
};

export const useCampaignCompareToDateRange = (
  id: string
): [
  CompareToDateRangeStateType,
  (range: CompareToDateRangeStateType) => void,
] => {
  const { compareToDateRanges, setCompareToDateRange } = React.useContext(
    CampaignDateRangeContext
  );
  const set = React.useCallback(
    (range: CompareToDateRangeStateType) => setCompareToDateRange(id, range),
    [id, setCompareToDateRange]
  );
  return [compareToDateRanges[id] ?? defaultCompareToDateRangeState, set];
};

export default CampaignDateRangeContext;
